import { AnimatedContentLoader, DoubleColumnLayout, DoubleColumnLayoutColumn } from '@components'
import { getProductPageUrl } from '@constants'
import { useBusinessContext } from '@context'
import { useQueryParam } from '@hooks'
import { collectFromPages, fetchProducts, IProduct } from '@query'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useInfiniteQuery } from '@tanstack/react-query'
import { useHistory } from 'react-router-dom'
import styled from 'styled-components'
import { ProductActionBar } from './ProductActionBar'
import { ProductDetails } from './ProductDetails'
import { ProductList } from './ProductList'

export const ProductPage: React.FC = () => {
  const [t] = useTranslation()
  const { slug } = useBusinessContext()
  const [selectedId] = useQueryParam<number>('id', value => parseInt(value, 10))
  const history = useHistory()
  const [search, setSearch] = useState('')

  const onClose = () => {
    const url = getProductPageUrl(slug)
    history.push(url)
  }

  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: [slug, 'products', { search }],
    initialPageParam: 1,
    queryFn: ({ pageParam = 1 }) => fetchProducts({ slug }, { search, page: pageParam }),
    getNextPageParam: lastPage => lastPage.next,
    getPreviousPageParam: lastPage => lastPage.prev
  })
  const products = collectFromPages<IProduct>(data)

  return (
    <DoubleColumnLayout
      header={t('products.mainTitle')}
      isRightVisible={Boolean(selectedId)}
      onRightClose={onClose}
    >
      <DoubleColumnLayoutColumn>
        <PageContentWrapper>
          <ProductActionBar onSearch={value => setSearch(value)} searchTerm={search} />

          <ProductListWrapper>
            <AnimatedContentLoader
              isLoading={isLoading}
              isEmpty={products.length === 0}
              isEmptyDescription={t('products.isEmptyDescription')}
            >
              {products && (
                <ProductList
                  products={products}
                  onScrollBottom={() => hasNextPage && !isFetchingNextPage && fetchNextPage()}
                />
              )}
            </AnimatedContentLoader>
          </ProductListWrapper>
        </PageContentWrapper>
      </DoubleColumnLayoutColumn>
      <DoubleColumnLayoutColumn innerKey={selectedId} isRight={true}>
        {selectedId && <ProductDetails key={selectedId} productId={selectedId} onClose={onClose} />}
      </DoubleColumnLayoutColumn>
    </DoubleColumnLayout>
  )
}
const PageContentWrapper = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
`

const ProductListWrapper = styled.div`
  flex: 1;
  flex-direction: column;
  overflow: hidden;
  position: relative;
`
